import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPolicy = () => {
  const { lang } = useLanguage();

  const content = {
    es: {
      title: "Política de Privacidad",
      sections: [
        {
          title: "1. Información que Recopilamos",
          text: "Recopilamos información que nos proporcionas directamente, como cuando nos contactas a través de nuestro sitio web, te suscribes a nuestro boletín, o interactúas con nuestro contenido. Esto puede incluir:\n\n• Nombre y dirección de email\n• Mensajes y comunicaciones que nos envías\n• Cualquier otra información que elijas proporcionar"
        },
        {
          title: "2. Cómo Usamos tu Información",
          text: "Usamos la información que recopilamos para:\n\n• Responder a tus consultas y proporcionar atención al cliente\n• Enviarte actualizaciones sobre nuestras obras y contenido (si te has suscrito)\n• Mejorar nuestro sitio web y servicios\n• Cumplir con obligaciones legales"
        },
        {
          title: "3. Compartir Información",
          text: "No vendemos, intercambiamos o transferimos de otra manera tu información personal a terceros sin tu consentimiento, excepto como se describe en esta política. Podemos compartir tu información con proveedores de servicios de confianza que nos asisten en operar nuestro sitio web y conducir nuestro negocio, siempre que acuerden mantener esta información confidencial."
        },
        {
          title: "4. Seguridad de Datos",
          text: "Implementamos medidas de seguridad apropiadas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por internet o almacenamiento electrónico es 100% seguro."
        },
        {
          title: "5. Cookies y Seguimiento",
          text: "Nuestro sitio web puede usar cookies y tecnologías de seguimiento similares para mejorar tu experiencia de navegación. Puedes elegir desactivar las cookies a través de la configuración de tu navegador, aunque esto puede afectar la funcionalidad de nuestro sitio web."
        },
        {
          title: "6. Enlaces de Terceros",
          text: "Nuestro sitio web contiene enlaces a sitios web de terceros. No somos responsables de las prácticas de privacidad de estos sitios externos y te animamos a revisar sus políticas de privacidad."
        },
        {
          title: "7. Tus Derechos",
          text: "Tienes derecho a:\n\n• Acceder a la información personal que tenemos sobre ti\n• Solicitar corrección de información inexacta\n• Solicitar eliminación de tu información personal\n• Optar por no recibir comunicaciones de marketing"
        },
        {
          title: "8. Cambios a Esta Política",
          text: "Podemos actualizar esta política de privacidad de vez en cuando. Te notificaremos de cualquier cambio publicando la nueva política en esta página y actualizando la fecha de \"Última actualización\"."
        },
        {
          title: "9. Contáctanos",
          text: "Si tienes alguna pregunta sobre esta política de privacidad o nuestras prácticas de datos, por favor contáctanos a través de la información de contacto proporcionada en nuestro sitio web."
        }
      ]
    },
    en: {
      title: "Privacy Policy",
      sections: [
        {
          title: "1. Information We Collect",
          text: "We collect information you provide directly to us, such as when you contact us through our website, subscribe to our newsletter, or interact with our content. This may include:\n\n• Name and email address\n• Messages and communications you send us\n• Any other information you choose to provide"
        },
        {
          title: "2. How We Use Your Information",
          text: "We use the information we collect to:\n\n• Respond to your inquiries and provide customer service\n• Send you updates about our artworks and content (if you have subscribed)\n• Improve our website and services\n• Comply with legal obligations"
        },
        {
          title: "3. Sharing Information",
          text: "We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information with trusted service providers who assist us in operating our website and conducting our business, as long as they agree to keep this information confidential."
        },
        {
          title: "4. Data Security",
          text: "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure."
        },
        {
          title: "5. Cookies and Tracking",
          text: "Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can choose to disable cookies through your browser settings, although this may affect the functionality of our website."
        },
        {
          title: "6. Third-Party Links",
          text: "Our website contains links to third-party websites. We are not responsible for the privacy practices of these external sites and encourage you to review their privacy policies."
        },
        {
          title: "7. Your Rights",
          text: "You have the right to:\n\n• Access the personal information we hold about you\n• Request correction of inaccurate information\n• Request deletion of your personal information\n• Opt-out of receiving marketing communications"
        },
        {
          title: "8. Changes to This Policy",
          text: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the \"Last updated\" date."
        },
        {
          title: "9. Contact Us",
          text: "If you have any questions about this privacy policy or our data practices, please contact us through the contact information provided on our website."
        }
      ]
    }
  };

  const currentContent = content[lang] || content.es;

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-gallery-bg">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        <h1 className="text-4xl md:text-5xl font-serif mb-12 text-center">{currentContent.title}</h1>
        
        <div className="space-y-12">
          {currentContent.sections.map((section, index) => (
            <section key={index} className="space-y-4">
              <h2 className="text-xl font-serif text-gallery-text">{section.title}</h2>
              <p className="text-gallery-muted leading-relaxed whitespace-pre-line font-light">
                {section.text}
              </p>
            </section>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-gallery-border text-center">
          <p className="text-xs uppercase tracking-widest text-gallery-muted">
            Last updated: January 2026
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;